// Prestin — wedge POC logic (TypeScript on Vite, ADR 0004).
// Deterministic core, the fuzzy read at the edge. Real PDF ingest now runs
// through src/ingest/ (pdf.js positioned extraction -> lines -> Tier-A fields
// -> line items, docs/15). Scanned PDFs/images are detected and routed to the
// OCR seam (next slice). Doctrine inherited from LexVet: abstain rather than
// invent, confirm the serial, everything downstream is rules.

import { ingestFile } from "./ingest/ingest";
import type { ParsedInvoice } from "./ingest/types";
import { runSelftest } from "./ingest/selftest";
import { runAdpSelftest } from "./adp/selftest";
import { computeFunding, ADP_MANUAL_ACTIONS } from "./adp";
import type { DeviceAid, FittingType } from "./adp";

// ---------- The clinic (would come from settings) ----------
const CLINIC = {
  name: "Clearwater Hearing",
  location: "North Bay, Ontario",
};

// ---------- Warranty + loss-and-damage rules table ----------
// Keyed by model family. These dates are filled by rule, not read off the invoice,
// because invoices rarely print them reliably. Real table is maintained per maker.
// Order matters: the accessory rule is checked first, because accessory names
// often contain a manufacturer word (e.g. "Phonak Charger Case Combi") that
// would otherwise match a device rule.
interface WarrantyRule { match: RegExp; family: string; warrantyYears: number; lossDamageYears: number; device: boolean }
const WARRANTY_RULES: WarrantyRule[] = [
  { match: /charger|case|dome|wax|filter|accessory|combi|cable|remote|dock/i, family: "Accessory", warrantyYears: 1, lossDamageYears: 0, device: false },
  { match: /lumity|audeo|paradise|phonak/i, family: "Phonak hearing aid", warrantyYears: 3, lossDamageYears: 3, device: true },
  { match: /resound|nexia|omnia/i,          family: "ReSound hearing aid", warrantyYears: 4, lossDamageYears: 3, device: true },
  { match: /oticon|intent|real/i,           family: "Oticon hearing aid",  warrantyYears: 3, lossDamageYears: 3, device: true },
];
function ruleFor(model: string): WarrantyRule {
  return WARRANTY_RULES.find(r => r.match.test(model)) ||
    { match: /$^/, family: "Device", warrantyYears: 2, lossDamageYears: 0, device: true };
}

// ---------- The sample invoice (mocked Phonak / Sonova) ----------
const SAMPLE_INVOICE = `SONOVA CANADA INC.  (Phonak)
4520 Dixie Road, Mississauga, ON  L4W 1V4

INVOICE

Invoice No:   PH-2026-558217
Invoice Date: 2026-06-18
Sold To:      Clearwater Hearing, North Bay ON
Account:      CW-4471
Channel:      Authorized account

Qty   Model                          Serial          Unit Price
1     Audeo Lumity L90-312           AUD2K8X41        1,840.00
1     Audeo Lumity L90-312           AUD2K8X63        1,840.00
1     Phonak Charger Case Combi      CHG77120         95.00

                              Subtotal               3,775.00
                              GST/HST (13%)            490.75
                              Total CAD              4,265.75
`;

// ---------- Shapes the review/result stages consume ----------
interface WItem { qty: number; model: string; serial: string; unitPrice: number }
interface WParsed { maker: string; invoiceNo: string; invoiceDate: string; items: WItem[] }

// ---------- The plain-text "read" (kept for the sample path) ----------
function parseInvoice(text: string): WParsed {
  const maker = (text.match(/^([A-Z][A-Za-z .,&()]+)/m) || [, "Manufacturer"])[1]!.trim();
  const invoiceNo = (text.match(/Invoice No:\s*([^\s].*)/i) || [, ""])[1]!.trim();
  const invoiceDate = (text.match(/Invoice Date:\s*([0-9-/]+)/i) || [, ""])[1]!.trim();

  const items: WItem[] = [];
  const lineRe = /^(\d+)\s+(.+?)\s+((?=[A-Z0-9]*\d)[A-Z0-9]{6,})\s+([\d,]+\.\d{2})\s*$/;
  for (const raw of text.split("\n")) {
    const m = raw.match(lineRe);
    if (!m) continue;
    items.push({
      qty: parseInt(m[1]!, 10),
      model: m[2]!.trim(),
      serial: m[3]!.trim(),
      unitPrice: parseFloat(m[4]!.replace(/,/g, "")),
    });
  }
  return { maker, invoiceNo, invoiceDate, items };
}

// ---------- the PHI boundary (structural, not a convention) ----------
// The ONLY thing that may leave the device for a model is the manufacturer
// invoice. Patient identity never crosses to a third-party model. The guard
// throws on anything else, so a future edit cannot silently widen what is
// sent. Wire any LLM read through modelReadPayload({ invoiceText }).
// See ../../decisions/0006-secure-ingest.md.
const MODEL_READ_ALLOWED_FIELDS = ["invoiceText", "maker", "invoiceNo", "invoiceDate", "items"] as const;
export function modelReadPayload(fields: Record<string, unknown>): Record<string, unknown> {
  const allowed = MODEL_READ_ALLOWED_FIELDS as readonly string[];
  const extra = Object.keys(fields).filter(k => !allowed.includes(k));
  if (extra.length) throw new Error("PHI boundary: refusing to send non-invoice fields to the model: " + extra.join(", "));
  const payload: Record<string, unknown> = {};
  for (const k of allowed) if (k in fields) payload[k] = fields[k];
  return payload;
}

// ---------- Date helpers ----------
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
function today(): Date { return new Date(); }
function fmtDate(d: Date): string { return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`; }
function addYears(d: Date, y: number): Date { const n = new Date(d); n.setFullYear(n.getFullYear() + y); return n; }
function money(n: number): string { return n.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

// ---------- State ----------
let parsed: WParsed = { maker: "", invoiceNo: "", invoiceDate: "", items: [] };
let confirmedSerials: (string | null)[] = [];
const receiveDate = today();

// ---------- DOM helpers ----------
function $(id: string): HTMLElement {
  const n = document.getElementById(id);
  if (!n) throw new Error("missing element #" + id);
  return n;
}

// ---------- Stage control ----------
const stages: Record<string, HTMLElement> = {
  intake: $("stage-intake"),
  review: $("stage-review"),
  result: $("stage-result"),
};
function show(stage: string): void {
  for (const k in stages) stages[k]!.hidden = (k !== stage);
  window.scrollTo({ top: 0, behavior: "smooth" });
}

// ---------- Intake ----------
const dz = $("dropzone");
const fileinput = $("fileinput") as HTMLInputElement;
$("usesample").addEventListener("click", () => startReview(parseInvoice(SAMPLE_INVOICE), SAMPLE_INVOICE, []));
dz.addEventListener("click", () => fileinput.click());
dz.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fileinput.click(); } });

let dragN = 0;
dz.addEventListener("dragenter", (e) => { if (hasFiles(e)) { e.preventDefault(); dragN++; dz.classList.add("dragging"); } });
dz.addEventListener("dragover", (e) => { if (hasFiles(e)) { e.preventDefault(); e.dataTransfer!.dropEffect = "copy"; } });
dz.addEventListener("dragleave", () => { if (--dragN <= 0) dz.classList.remove("dragging"); });
dz.addEventListener("drop", (e) => {
  e.preventDefault(); dragN = 0; dz.classList.remove("dragging");
  const f = e.dataTransfer?.files?.[0];
  if (f) void handleFile(f);
});
fileinput.addEventListener("change", () => {
  const f = fileinput.files?.[0];
  if (f) void handleFile(f);
  fileinput.value = "";
});
function hasFiles(e: DragEvent): boolean { return !!e.dataTransfer && Array.from(e.dataTransfer.types || []).includes("Files"); }

// ---------- Real ingest (pdf.js pipeline; on-device) ----------
async function handleFile(f: File): Promise<void> {
  dz.classList.add("busy");
  try {
    const result = await ingestFile(await f.arrayBuffer());
    if (result.kind === "unsupported") { alert(result.reason); return; }
    if (result.kind === "scanned") { alert(result.note); return; }
    startReview(adapt(result), result.linesText, reviewNotes(result));
  } catch (err) {
    alert("Could not read that file: " + String(err));
  } finally {
    dz.classList.remove("busy");
  }
}

function adapt(r: ParsedInvoice): WParsed {
  return {
    maker: r.maker.value,
    invoiceNo: r.invoiceNo.value,
    invoiceDate: r.invoiceDate.value,
    items: r.items.map(it => ({
      qty: it.qty.value,
      model: it.model.value,
      serial: it.serial.value ?? "",
      unitPrice: it.unitPrice.value,
    })),
  };
}

function reviewNotes(r: ParsedInvoice): string[] {
  const notes = [...r.warnings];
  if (r.arithmeticOk) notes.unshift("Arithmetic check passed: line items sum to the printed subtotal.");
  const low: string[] = [];
  if (r.invoiceNo.confidence < 0.8) low.push("invoice number");
  if (r.invoiceDate.confidence < 0.8) low.push("invoice date");
  for (const it of r.items) if (it.serial.confidence < 0.8) low.push(`serial for "${it.model.value}"`);
  if (low.length) notes.push("Read with lower confidence — check: " + low.join(", ") + ".");
  return notes;
}

// ---------- Review ----------
function startReview(p: WParsed, specimenText: string, notes: string[]): void {
  parsed = p;
  confirmedSerials = parsed.items.map(() => null);

  $("specimen").textContent = specimenText;
  $("inv-meta").textContent = `${parsed.invoiceNo} · ${parsed.invoiceDate}`;

  renderNotes(notes);
  renderLineItems();
  updateGate();
  show("review");
}

function renderNotes(notes: string[]): void {
  document.getElementById("readnotes")?.remove();
  if (!notes.length) return;
  const box = el("div", { class: "readnotes", id: "readnotes" },
    ...notes.map(n => el("p", { class: "readnote", text: n })));
  const specimen = $("specimen");
  specimen.parentElement!.insertBefore(box, specimen);
}

function renderLineItems(): void {
  const root = $("lineitems");
  clear(root);
  parsed.items.forEach((it, i) => {
    const rule = ruleFor(it.model);
    const wEnd = addYears(receiveDate, rule.warrantyYears);
    const ldEnd = rule.lossDamageYears ? addYears(receiveDate, rule.lossDamageYears) : null;
    const field = (k: string, v: string, derived?: boolean) => el("div", { class: "field" },
      el("span", { class: "field-k", text: k }),
      el("span", { class: derived ? "field-v derived" : "field-v", text: v }));

    root.appendChild(el("div", { class: "lineitem" },
      el("p", { class: "li-model", text: it.model }),
      el("p", { class: "li-maker", text: `${rule.family} · qty ${it.qty}` }),
      field("Unit cost", `$${money(it.unitPrice)}`),
      field("Warranty ends", fmtDate(wEnd), true),
      field("Loss & damage ends", ldEnd ? fmtDate(ldEnd) : "—", true),
      rule.device ? serialBlock(it, i)
        : el("p", { class: "serial-warn", text: "Accessory — no serial confirmation needed." })
    ));
  });
}

function serialBlock(it: WItem, i: number): HTMLElement {
  const input = el("input", { type: "text", id: `serial-${i}`, value: it.serial,
    "aria-label": `Serial number for ${it.model}` }) as HTMLInputElement;
  const check = el("input", { type: "checkbox", id: `check-${i}` }) as HTMLInputElement;
  const box = el("div", { class: "serialconfirm", id: `sc-${i}` },
    el("div", { class: "serial-row" }, input),
    el("label", { class: "serial-label", for: `check-${i}` }, check, " I confirmed this serial against the invoice"),
    el("p", { class: "serial-warn", text: "The one detail Prestin never assumes. A single misread serial is silent and costly." })
  );
  const sync = () => {
    confirmedSerials[i] = check.checked ? input.value.trim() : null;
    box.classList.toggle("confirmed", check.checked && input.value.trim().length > 0);
    updateGate();
  };
  input.addEventListener("input", () => { if (check.checked) sync(); });
  check.addEventListener("change", sync);
  return box;
}

function deviceIndices(): number[] {
  return parsed.items.map((it, i) => ruleFor(it.model).device ? i : -1).filter(i => i >= 0);
}
function allConfirmed(): boolean {
  return deviceIndices().every(i => {
    const s = confirmedSerials[i];
    return !!s && s.length > 0;
  });
}
function updateGate(): void {
  const ready = allConfirmed();
  const btn = $("toresult") as HTMLButtonElement;
  const note = $("confirmnote");
  btn.disabled = !ready;
  const n = deviceIndices().length;
  const done = deviceIndices().filter(i => confirmedSerials[i]).length;
  note.textContent = ready ? `All ${n} serials confirmed. Ready.` : `Confirm every serial to continue. (${done} of ${n})`;
  note.classList.toggle("ready", ready);
}

$("backtointake").addEventListener("click", () => show("intake"));
$("toresult").addEventListener("click", () => { if (allConfirmed()) buildResult(); });
$("backtoreview").addEventListener("click", () => show("review"));
$("printbtn").addEventListener("click", () => window.print());

// ---------- Result documents ----------
interface DocItem extends WItem { family: string; device: boolean; warrantyEnd: Date; lossDamageEnd: Date | null }

function buildResult(): void {
  const items: DocItem[] = parsed.items.map((it, i) => {
    const rule = ruleFor(it.model);
    return {
      ...it,
      serial: rule.device ? (confirmedSerials[i] ?? "") : it.serial,
      family: rule.family,
      device: rule.device,
      warrantyEnd: addYears(receiveDate, rule.warrantyYears),
      lossDamageEnd: rule.lossDamageYears ? addYears(receiveDate, rule.lossDamageYears) : null,
    };
  });
  const total = items.reduce((s, it) => s + it.unitPrice * it.qty, 0);

  const rc = $("receiving"); clear(rc); rc.appendChild(buildReceiving(items, total));
  const pi = $("patientinvoice"); clear(pi); pi.appendChild(buildPatientInvoice(items));
  const ad = $("adpdraft"); clear(ad); ad.appendChild(buildAdpDraft(items));
  show("result");
}

function docHead(title: string, sub: string): HTMLElement {
  return el("div", { class: "doc-head" },
    el("div", null, el("h2", { class: "doc-title", text: title }), el("p", { class: "doc-sub", text: sub })),
    el("div", { class: "doc-org" }, el("b", { text: CLINIC.name }), CLINIC.location));
}
function field(k: string, v: string): HTMLElement {
  return el("div", { class: "field" }, el("span", { class: "field-k", text: k }), el("span", { class: "field-v", text: v }));
}
function subMono(t: string): HTMLElement { return el("span", { class: "mono", style: "font-size:12px;color:var(--muted)", text: t }); }

function buildReceiving(items: DocItem[], total: number): DocumentFragment {
  const rows = items.map(it => el("tr", null,
    el("td", null, it.model, el("br"), subMono(it.family)),
    el("td", { class: "mono", text: it.serial }),
    el("td", { class: "num", text: String(it.qty) }),
    el("td", { class: "num", text: `$${money(it.unitPrice)}` }),
    el("td", { text: it.device ? fmtDate(it.warrantyEnd) : "—" }),
    el("td", { text: it.lossDamageEnd ? fmtDate(it.lossDamageEnd) : "—" })
  ));
  const head = ["Item", "Serial", "Qty", "Unit cost", "Warranty ends", "Loss & damage ends"];
  return frag(
    docHead("Receiving Record", "Stock received into inventory"),
    field("Manufacturer invoice", `${parsed.maker} · ${parsed.invoiceNo} · ${parsed.invoiceDate}`),
    field("Received", fmtDate(receiveDate)),
    el("table", { class: "doc-table" },
      el("thead", null, el("tr", null, ...head.map(h => el("th", { text: h })))),
      el("tbody", null, ...rows)),
    el("div", { class: "doc-total" }, el("span", { text: "Total cost" }), el("span", { class: "num", text: `$${money(total)} CAD` })),
    el("p", { class: "doc-foot", text: `Serials confirmed by a person on ${fmtDate(receiveDate)}. Warranty and loss-and-damage dates filled from the maintained rules table, measured from the receive date.` })
  );
}

function buildPatientInvoice(items: DocItem[]): DocumentFragment {
  const rows = items.filter(it => it.device).map(it => el("tr", null,
    el("td", null, it.model, el("br"), subMono(`Serial ${it.serial}`)),
    el("td", { text: fmtDate(it.warrantyEnd) }),
    el("td", { text: it.lossDamageEnd ? fmtDate(it.lossDamageEnd) : "—" }),
    el("td", { class: "num", text: "$ —" })
  ));
  const head = ["Device", "Warranty ends", "Loss & damage ends", "Price"];
  return frag(
    docHead("Patient Invoice", "Template · clinic sets pricing & funding"),
    field("Patient", "________________________"),
    field("Date of service", fmtDate(receiveDate)),
    el("table", { class: "doc-table" },
      el("thead", null, el("tr", null, ...head.map(h => el("th", { text: h })))),
      el("tbody", null, ...rows)),
    el("p", { class: "doc-foot", text: "ADP funding split is drafted below from this invoice; WSIB / VAC / NIHB / private follow the same pattern. Price and funding are set per clinic; this template carries the device, serial, and warranty and loss-and-damage dates forward automatically." })
  );
}

// ---------- ADP claim draft (docs/17-adp-claim.md) ----------
// The wedge is receiving-only — it never collects patient identity, so this
// is deliberately a partial draft: the money math and device fields a
// received invoice can support on its own, plus an honest list of what the
// patient record still has to supply before the real application (014-3224-67)
// can be filed. See docs/17 for the full field-by-field model.
function inferFittingType(items: DocItem[]): { type: FittingType; note: string } {
  const devices = items.filter(it => it.device);
  if (devices.some(it => /\bfm\b|roger|remote mic|soundfield/i.test(it.model))) {
    return { type: "fm", note: "Inferred from an FM/remote-mic device on this invoice." };
  }
  if (devices.length >= 2) return { type: "binaural", note: `Inferred from ${devices.length} hearing aids on this receiving batch.` };
  return { type: "monaural", note: "Inferred from a single hearing aid on this receiving batch." };
}

function buildAdpDraft(items: DocItem[]): DocumentFragment {
  const devices = items.filter(it => it.device);
  const { type: fittingType, note: fittingNote } = inferFittingType(items);
  const aids: DeviceAid[] = devices.map(it => ({ make: parsed.maker, model: it.model, serial: it.serial, costCents: Math.round(it.unitPrice * 100) }));
  const funding = computeFunding(fittingType, aids);

  const stillNeeded = [
    "Patient identity: name, OHIP health number, date of birth, address.",
    "Authorizer: audiologist name and CASLPO registration number.",
    "This clinic's ADP vendor number, if not already on file.",
    "Confirmation this client is outside their 5-year funding period for this fitting type.",
  ];

  if (!devices.length) {
    return frag(
      docHead("ADP Claim (Draft)", "Estimated funding split"),
      el("p", { class: "doc-foot", text: "No hearing aid on this invoice — nothing to fund under ADP." })
    );
  }

  return frag(
    docHead("ADP Claim (Draft)", "Estimated funding split · form 014-3224-67"),
    field("Fitting type (inferred)", `${fittingType[0]!.toUpperCase()}${fittingType.slice(1)} — ${fittingNote}`),
    field("Total device cost", `$${money(funding.totalCostCents / 100)}`),
    el("div", { class: "doc-total" }, el("span", { text: "ADP contribution (75%, capped)" }), el("span", { class: "num", text: `$${money(funding.contributionCents / 100)}` })),
    el("div", { class: "doc-total" }, el("span", { text: "Client portion" }), el("span", { class: "num", text: `$${money(funding.clientPaysCents / 100)}` })),
    funding.cappedOut
      ? el("p", { class: "serial-warn", text: `Capped: ADP pays a maximum of $${money(ADP_CAP_DOLLARS[fittingType])} for a ${fittingType} fitting.` })
      : null,
    el("p", { class: "doc-foot-label", text: "Still needed before this can be filed" }),
    el("ul", { class: "adp-todo" }, ...[...stillNeeded, ...ADP_MANUAL_ACTIONS].map(t => el("li", { text: t }))),
    el("p", { class: "doc-foot", text: "Drafted from the received invoice alone, on this device. Nothing here has been sent anywhere — the government submission is a file upload a person completes at GO Secure / MCEDT. See docs/17-adp-claim.md." })
  );
}
const ADP_CAP_DOLLARS: Record<FittingType, number> = { monaural: 500, binaural: 1000, fm: 1350 };

// ---------- safe DOM construction ----------
// Every value that originates from the invoice read is untrusted. We build the
// DOM with createElement and set every dynamic value through textContent, so
// invoice content can never become markup, script, or an attribute payload.
// No string-concatenated innerHTML on this path. See ADR 0006.
type Kid = Node | string | number | null | false | undefined;
function el(tag: string, attrs?: Record<string, unknown> | null, ...kids: Kid[]): HTMLElement {
  const n = document.createElement(tag);
  if (attrs) for (const k in attrs) {
    const v = attrs[k];
    if (v == null) continue;
    if (k === "class") n.className = String(v);
    else if (k === "text") n.textContent = String(v);          // dynamic values go here, always
    else if (k.startsWith("on") && typeof v === "function") n.addEventListener(k.slice(2), v as EventListener);
    else n.setAttribute(k, String(v));                          // attribute names are literals, never data
  }
  for (const kid of kids) {
    if (kid == null || kid === false) continue;
    n.append(kid instanceof Node ? kid : document.createTextNode(String(kid)));
  }
  return n;
}
function clear(node: HTMLElement): void { while (node.firstChild) node.removeChild(node.firstChild); }
function frag(...kids: Kid[]): DocumentFragment {
  const f = document.createDocumentFragment();
  for (const k of kids) { if (k == null || k === false) continue; f.append(k instanceof Node ? k : document.createTextNode(String(k))); }
  return f;
}

// ---------- golden-fixture selftest (?selftest) ----------
if (new URLSearchParams(location.search).has("selftest")) {
  void runSelftest().then(reports => {
    const pass = reports.filter(r => r.pass).length;
    console.log(`[selftest] ${pass}/${reports.length} fixtures pass`);
    for (const r of reports) console.log(`[selftest] ${r.pass ? "PASS" : "FAIL"} ${r.name}`, r.diffs);
    const badge = el("div", {
      id: "selftest-badge",
      style: "position:fixed;bottom:12px;right:12px;z-index:99;padding:10px 14px;border-radius:9px;" +
        "font:500 13px/1.4 system-ui;color:#fff;background:" + (pass === reports.length ? "#2c6e49" : "#8c2f39"),
      text: `selftest ${pass}/${reports.length}` + (pass === reports.length ? " ✓" : " — see console"),
    });
    document.body.appendChild(badge);
    (window as unknown as Record<string, unknown>).__selftest = reports;
  });
}

// ---------- ADP core selftest (?adptest) ----------
if (new URLSearchParams(location.search).has("adptest")) {
  const reports = runAdpSelftest();
  const pass = reports.filter(r => r.pass).length;
  console.log(`[adptest] ${pass}/${reports.length} cases pass`);
  for (const r of reports) console.log(`[adptest] ${r.pass ? "PASS" : "FAIL"} ${r.name}`, r.diffs);
  const badge = el("div", {
    id: "adptest-badge",
    style: "position:fixed;bottom:12px;left:12px;z-index:99;padding:10px 14px;border-radius:9px;" +
      "font:500 13px/1.4 system-ui;color:#fff;background:" + (pass === reports.length ? "#2c6e49" : "#8c2f39"),
    text: `adptest ${pass}/${reports.length}` + (pass === reports.length ? " ✓" : " — see console"),
  });
  document.body.appendChild(badge);
  (window as unknown as Record<string, unknown>).__adptest = reports;
}
